/**
 * Unified Profile types resource (CT-5277).
 *
 * Real signatures and snake_case bodies; only the transport is stubbed via
 * `mockTransport.js`. When an endpoint lands, change nothing here except the
 * path string if the backend team renames it.
 */

import { validateApiResponse } from '../shared/utils/apiResponse.js';
import { request } from './mockTransport.js';

export { __resetMockProfileTypesForTests } from './__mocks__/profileTypes.mock.js';

/**
 * Map a raw API type onto the shape the hub renders. Tolerates camelCase so the
 * mapper survives a backend that does not settle on snake_case everywhere.
 *
 * No entity-type branch, lookup table or per-type default lives here, and none
 * ever should: which source column feeds each key is decided by the Data Studio
 * mapping on the backend. The frontend maps statically.
 */
export const mapApiProfileTypeToUi = (raw = {}) => ({
  id: raw.entity_type ?? raw.entityType ?? raw.id ?? '',
  label: raw.label ?? raw.name ?? '',
  visual: raw.visual ?? raw.icon_url ?? raw.iconUrl ?? null,
  count: raw.count ?? raw.total ?? null,
});

/**
 * List the Unified Profile types the caller may see.
 *
 * Filtering is the backend's job — there is no client-side permission primitive
 * in this platform and this layer must not invent one. What comes back is what
 * the user may see.
 *
 * Paged and searchable on the SERVER, following the same body shape as
 * `listEntities`. The type set is whatever a tenant publishes in Data Studio,
 * so "there will only ever be a handful" is an assumption nobody has checked —
 * and the hub is the domain's landing screen, which makes it the worst place to
 * discover it was wrong.
 *
 * TODO(CT-5277 / backend): `POST /v1/workspace/profiles/types/list` with
 * `{ page, page_size, order_by, order_direction, search }`.
 */
export const listProfileTypes = async ({
  page = 1,
  pageSize = 12,
  orderBy = 'label',
  orderDirection = 'asc',
  search,
} = {}) => {
  const response = await request('/v0/profile/types/list', {
    page,
    page_size: pageSize,
    order_by: orderBy,
    order_direction: orderDirection,
    ...(search ? { search } : {}),
  });
  return validateApiResponse(response, 'Failed to load Unified Profile types');
};
